import { createContext, createElement, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { getPlaybackState, patchPlaybackState, type PlaybackPatch } from '../../api/playback.ts';
import { realtimeClient } from '../../api/realtime.ts';
import type { PlaybackState } from '../../api/types.ts';
import { computeClockOffsetMs, extrapolatePositionMs } from './clock.ts';
import {
  toEpochMs,
  describeControlWriteError,
  withPositionStamp,
  isFreshEnoughToApply,
  seqOf as seqOfPure,
  serverNowMs,
  toStageSyncPayload,
  shouldRebroadcastFrame,
  shouldPollForFallback,
  shouldRunPollFallback,
  shouldPostHeartbeat,
} from './playbackMath.ts';
import { getStageChannel } from './stageChannel.ts';
import { apiErrorMessage } from '../../api/errorMessage.ts';
import { useT } from '../../i18n/i18n.tsx';

/**
 * The one read model for `playback-state` shared by the operator cockpit and the Stage window
 * (AC-OPS-2/AC-OPS-4). Both realms run this SAME hook — only the cockpit ever calls `patch()`;
 * the Stage is purely a reader, which is what keeps "only one window decodes the media" (AC-OPS-2)
 * true by construction rather than by convention.
 *
 * Freshness, cheapest path first:
 *  1. `BroadcastChannel` 'state' messages — same-machine, sub-millisecond (AC-OPS-2's primary path).
 *     Carries only the numeric/enum fields (`StageSyncPayload`), never the populated `song`/
 *     `queueEntry` relations, so a song CHANGE (not just a position/key/volume tick) triggers a
 *     `refresh()` to pick up the new relations rather than trusting an un-authenticated payload for
 *     them (see stageChannel.ts's own doc comment).
 *  2. The realtime SSE 'playback' channel — durable, cross-device, this realm's own connection.
 *  3. `GET /api/playback/state` on mount and whenever (1)/(2) signal a song change.
 * A monotonic `seq` guard on all three paths means an out-of-order frame can never regress state.
 *
 * R11-P1-2 fix (round 11, live-Chrome critic): `role` decides whether the HTTP poll-fallback
 * effect near the bottom of this hook ever runs. Confirmed live: the cockpit — three SEPARATE
 * instances of this hook (`App.tsx`'s `ConsoleShell`, `OperatorConsole.tsx`'s seed, and
 * `usePlaybackEngine.ts`'s own internal one), each with a live SSE connection — polled
 * `GET /api/playback/state` roughly 24x/min FOREVER, because `lastBroadcastAtRef` is only ever
 * refreshed by an INCOMING BroadcastChannel 'state' message, and a `BroadcastChannel` never echoes
 * a post back to the tab that sent it — so `shouldPollForFallback` read as permanently "stale" in
 * every realm that only ever POSTS to that channel and never separately receives its own frames
 * back. The Stage window (`StageRoot.tsx`) is the one realm with NO SSE connection of its own
 * (`App.tsx`'s realtime client is console-only) and genuinely needs this fallback — it keeps
 * calling `usePlaybackState()` with no `role` argument at all, so `'stage'` stays the default
 * rather than a call site elsewhere in the app silently losing its safety net. The console side is
 * now consolidated onto exactly ONE instance via `PlaybackStateProvider`/`usePlaybackStateContext`
 * below, which always requests `'console'` — so the fix here is two-layered: role-gating the poll
 * AND cutting three redundant instances down to one.
 */
export function usePlaybackState(role: 'console' | 'stage' = 'stage') {
  const t = useT();
  const [state, setState] = useState<PlaybackState | null>(null);
  const [clockOffsetMs, setClockOffsetMs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // P0 fix (round 6): `patch()` used to reject straight through to every fire-and-forget
  // `void patch(...)` caller in `usePlaybackEngine.ts` — with nothing there ever attaching a
  // `.catch()`, a failed write (most commonly the server's 4/s rate limit tripped by the stall-
  // watchdog loop, see that file's own doc comment) became an unhandled promise rejection logged
  // straight to the console instead of readable copy (confirmed live: 342 of them in one session).
  // `patch()` below now NEVER rejects — every caller that cares about success checks the resolved
  // value (`null` on failure) instead — and this state is where the "why" lives for whoever wants
  // to show it (`OperatorConsole.tsx`).
  const [patchError, setPatchError] = useState<{ message: string; retryAfterSec?: number } | null>(null);
  const stateRef = useRef<PlaybackState | null>(null);
  stateRef.current = state;
  // R10-P1-1 fix (round 10): kept fresh every render (mirrors `stateRef` above) so `patch()` — a
  // stable `useCallback` with an empty dep array — can read the LATEST clock offset without being
  // recreated on every `computeClockOffsetMs` tick, which would otherwise change every caller's own
  // memoized reference to it (`usePlaybackEngine.ts`'s callbacks all depend on `patch`).
  const clockOffsetMsRef = useRef(0);
  clockOffsetMsRef.current = clockOffsetMs;
  // R10-P1-3 fix (round 10): the last `seq` this realm has already posted to the stage
  // BroadcastChannel, from EITHER `patch()`'s own broadcast or the SSE-frame rebroadcast below —
  // shared between the two so the SSE echo of a write this cockpit just made itself is never
  // posted twice under two different code paths. See `shouldRebroadcastFrame`'s own doc comment.
  const lastBroadcastSeqRef = useRef<number | string | null>(null);
  // R10-P1-3 fix (round 10): the wall-clock time of the last BroadcastChannel 'state' message this
  // realm has seen — the Stage's poll-fallback effect below reads this to decide when its primary,
  // same-machine sync path has gone quiet. Starts at "just mounted" so a brand-new tab does not
  // immediately fire a redundant poll on top of its own `refresh()` above.
  const lastBroadcastAtRef = useRef(Date.now());

  // R9-P2-C fix (round 9): now shared with `patch()`'s own ordering guard below — see
  // `playbackMath.ts`'s `seqOf`/`isFreshEnoughToApply` doc comments.
  const seqOf = seqOfPure;

  const refresh = useCallback(async () => {
    const requestStartMs = Date.now();
    try {
      const { state: fresh, serverTimeMs } = await getPlaybackState();
      const requestEndMs = Date.now();
      if (seqOf(fresh) < seqOf(stateRef.current)) return; // a slower, now-stale request lost the race
      setState(fresh);
      setClockOffsetMs(computeClockOffsetMs(serverTimeMs, requestStartMs, requestEndMs));
      setError(null);
    } catch (err) {
      // MVP-344: nothing destructures `error` today (`patchError` is the one the cockpit shows).
      // Kept rather than deleted — a read hook that cannot say why its fetch failed is the gap
      // `useSingerDirectory` was rewritten to close in MVP-330 — but fixed to the same rule as
      // every other sink so wiring it up later cannot resurrect `[object Object]`. See
      // `api/errorMessage.ts`.
      setError(apiErrorMessage(err, t('common.serverUnreachableRetry')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // P2 fix (round 5): same StrictMode double-invoke useRotation.ts/useSessionInfo.ts already
  // document — `GET /api/playback/state` was confirmed live firing twice per mount. No per-call
  // identity to key on (this hook takes no params), so a plain "already ran" ref stands in;
  // `refresh` stays freely callable afterwards for the BroadcastChannel/SSE paths below.
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    void refresh();
  }, [refresh]);

  // Path 1: BroadcastChannel — the fast, same-machine path.
  useEffect(() => {
    const channel = getStageChannel();
    return channel.onMessage((message) => {
      // R12-P2-A fix (round 12, live-Chrome critic): a heartbeat carries no state to apply — it
      // exists purely to prove the console-side realm is alive right now, same as any 'state'
      // frame does for the poll-fallback clock below (see `shouldPostHeartbeat`'s own doc comment
      // in playbackMath.ts for why the console posts these while genuinely paused).
      if (message.type === 'heartbeat') {
        lastBroadcastAtRef.current = Date.now();
        return;
      }
      if (message.type !== 'state') return;
      // R10-P1-3 fix (round 10): ANY 'state' frame, stale or not, proves the primary same-machine
      // path is alive right now — the poll-fallback effect below only ever needs to know "have I
      // heard anything recently", not "did I apply the last thing I heard".
      lastBroadcastAtRef.current = Date.now();
      const payload = message.payload;
      if (seqOf(payload) < seqOf(stateRef.current)) return;
      const current = stateRef.current;
      const songChanged = (current?.song?.documentId ?? null) !== payload.songDocumentId;
      if (songChanged || !current) {
        void refresh();
        return;
      }
      setState({
        ...current,
        mode: payload.mode,
        isPlaying: payload.isPlaying,
        positionMs: payload.positionMs,
        positionAtServerMs: payload.positionAtServerMs,
        keyShift: payload.keyShift,
        tempoPct: payload.tempoPct,
        vocalChannel: payload.vocalChannel,
        volume: payload.volume,
        lyricOffsetMs: payload.lyricOffsetMs,
        // R8-P1-3 fix (round 8, live-Chrome critic): without these two, a same-song update over
        // this channel never touched a stale `errorCode`/`errorMessage` already sitting in
        // `current` — only a song change (which takes the `refresh()` branch above) or a hard
        // reload ever picked up the server's cleared value. See `stageChannel.ts`'s own doc
        // comment on these fields for the hold-card symptom this closes.
        errorCode: payload.errorCode,
        errorMessage: payload.errorMessage,
        seq: payload.seq,
      });
    });
  }, [refresh]);

  // Path 2: the realtime SSE 'playback' channel — durable, cross-device fallback.
  //
  // R10-P1-3 fix (round 10, live-Chrome critic): this used to ONLY `setState` — the Stage window
  // never opens its own SSE connection (`App.tsx`'s realtime client is console-only by design), so
  // a playback-state change reaching THIS realm over SSE (an API-driven edit, a second cockpit tab,
  // anything not routed through this tab's own `patch()`) had no way to reach the Stage at all until
  // a full reload re-ran `refresh()`. This realm's own SSE connection is the only place that can
  // relay it onward.
  useEffect(() => {
    return realtimeClient.onFrame((frame) => {
      // MVP-483: same one-line reaction `OperatorConsole.tsx`/`RequestPage.tsx` already had, and
      // that `useSessionInfo`/`useRotationState` gain in this task — a reconnect means every
      // `playback` frame published during the drop was missed, and `refresh()`'s own `seqOf` guard
      // means a response that turns out to be older than what is already showing is discarded
      // rather than regressing the cockpit mid-song.
      if (frame.event === 'hello') {
        void refresh();
        return;
      }
      if (frame.channel !== 'playback') return;
      // R11-P1-2 fix (round 11, live-Chrome critic): a live SSE frame is just as much proof this
      // realm has a working, up-to-date connection as a BroadcastChannel message is — see this
      // hook's own doc comment on `role` for why `lastBroadcastAtRef` staying frozen forever (only
      // ever refreshed by an INCOMING BroadcastChannel post, which a realm never receives an echo
      // of its own) was the other half of the permanent-poll bug, independent of the role gate
      // below.
      lastBroadcastAtRef.current = Date.now();
      const payload = frame.payload as PlaybackState;
      if (seqOf(payload) < seqOf(stateRef.current)) return;
      setState(payload);
      if (shouldRebroadcastFrame(payload.seq, lastBroadcastSeqRef.current)) {
        lastBroadcastSeqRef.current = payload.seq;
        getStageChannel().post({ type: 'state', payload: toStageSyncPayload(payload), sentAtMs: Date.now() });
      }
    });
  }, [refresh]);

  // R10-P1-3 fix (round 10, live-Chrome critic): the Stage's primary path (BroadcastChannel) is
  // same-machine-only — a popup under a different OS profile, or a genuinely separate device acting
  // as the Stage, has no live signal at all otherwise. Polls the durable HTTP fallback once the
  // BroadcastChannel has gone quiet for too long; `refresh()`'s own `seqOf` guard means a stale poll
  // response racing a fresher BroadcastChannel/SSE frame can never regress what is already showing.
  //
  // R11-P1-2 fix (round 11, live-Chrome critic): gated on `role === 'stage'` — the cockpit ALWAYS
  // has its own live SSE connection (`App.tsx` starts `realtimeClient` for the console route only)
  // and must never poll at all, per this hook's own doc comment on `role`.
  useEffect(() => {
    if (!shouldRunPollFallback(role)) return undefined;
    const id = window.setInterval(() => {
      if (shouldPollForFallback(lastBroadcastAtRef.current, Date.now())) void refresh();
    }, 5000);
    return () => window.clearInterval(id);
  }, [refresh, role]);

  // R12-P2-A fix (round 12, live-Chrome critic): the Stage's poll fallback above fires whenever no
  // BroadcastChannel 'state' frame has arrived in 10s — which is exactly what a genuinely PAUSED
  // cockpit looks like (nothing new to broadcast), even though its SSE connection is perfectly
  // alive. Critic measured 12 `GET /api/playback/state` polls/min in that state. The console posts
  // a lightweight `{type:'heartbeat'}` on the same channel every ~4s purely to keep the Stage's
  // "have I heard anything recently" clock fresh (handled in Path 1's `onMessage` above) — well
  // inside the 10s poll threshold, so a paused-but-open cockpit suppresses the poll entirely.
  const heartbeatSeqRef = useRef(0);
  useEffect(() => {
    if (!shouldPostHeartbeat(role)) return undefined;
    const id = window.setInterval(() => {
      heartbeatSeqRef.current += 1;
      getStageChannel().post({ type: 'heartbeat', seq: heartbeatSeqRef.current, sentAtMs: Date.now() });
    }, 4000);
    return () => window.clearInterval(id);
  }, [role]);

  const patch = useCallback(async (body: PlaybackPatch): Promise<PlaybackState | null> => {
    try {
      // R9-P1-A fix: stamp `positionAtServerMs` on any write touching `positionMs`/`isPlaying`
      // that did not already carry its own — see `withPositionStamp`'s own doc comment.
      // R10-P1-1 fix (round 10): the stamp is now in the SERVER's clock domain
      // (`serverNowMs`/`clockOffsetMsRef`), not the local wall clock — see `serverNowMs`'s own doc
      // comment for the systematic bias this closes.
      const { state: updated } = await patchPlaybackState(withPositionStamp(body, serverNowMs(Date.now(), clockOffsetMsRef.current)));
      setPatchError(null);
      // R9-P2-C fix (round 9): only apply this response if it is not older than what is already
      // showing — two `patch()` calls in flight at once have no guarantee their HTTP responses
      // resolve in request order (see `isFreshEnoughToApply`'s own doc comment for the Stage
      // hold-card miss this closes). The call still returns the fresh row either way — every
      // caller only checks it for truthiness/failure, never trusts it as "the current state".
      if (isFreshEnoughToApply(updated, stateRef.current)) {
        setState(updated);
        lastBroadcastSeqRef.current = updated.seq;
        // R11-P1-2 fix (round 11, live-Chrome critic): this realm's OWN successful write is live
        // proof of state just as fresh as an incoming BroadcastChannel message — see this hook's
        // own doc comment on `role` for why leaving this unrefreshed was half of the permanent-poll
        // bug (the other half is `role`-gating the poll effect itself, below).
        lastBroadcastAtRef.current = Date.now();
        getStageChannel().post({ type: 'state', payload: toStageSyncPayload(updated), sentAtMs: Date.now() });
      }
      return updated;
    } catch (err) {
      // Never rejects — see this hook's own doc comment above `patchError` for why. Every
      // fire-and-forget `void patch(...)` caller stays safe by construction; a caller that needs
      // to know about the failure (e.g. OperatorConsole.tsx's `advance()`) checks for `null`.
      // MVP-389: `t` is passed in — the three sentences this used to build were English in the
      // cockpit's own error banner, on a Thai-default UI.
      setPatchError(describeControlWriteError(err, t));
      return null;
    }
  }, [t]);

  /** The position "right now" — ticks the caller's own re-render (via `nowMs`) rather than this
   * hook re-rendering on a timer itself, so a numeric-only readout (cockpit) can poll it at 1Hz
   * while a future video-driving consumer could call it every frame with no wasted work here. */
  const extrapolatedPositionMs = useCallback(
    (nowMs: number = Date.now()): number => {
      if (!state) return 0;
      return extrapolatePositionMs(
        {
          positionMs: state.positionMs,
          positionAtServerMs: toEpochMs(state.positionAtServerMs),
          isPlaying: state.isPlaying,
          durationMs: state.song?.durationMs ?? null,
        },
        nowMs,
        clockOffsetMs
      );
    },
    [state, clockOffsetMs]
  );

  return { state, clockOffsetMs, loading, error, patchError, refresh, patch, extrapolatedPositionMs };
}

export type PlaybackStateHook = ReturnType<typeof usePlaybackState>;

/**
 * R11-P1-2 fix (round 11, live-Chrome critic): the cockpit used to run THREE independent
 * `usePlaybackState()` instances (`App.tsx`'s `ConsoleShell`, `OperatorConsole.tsx`'s seed, and
 * `usePlaybackEngine.ts`'s own copy) — three separate mount-time `GET /api/playback/state`
 * fetches, three separate SSE listeners, and (before the `role` gate on the hook itself) three
 * separate poll-fallback intervals all racing to describe the SAME server row. `ConsoleShell`
 * (`App.tsx`) mounts exactly one of these, wrapping every console-side consumer, so all three call
 * sites read and write through the ONE instance via `usePlaybackStateContext` below instead. The
 * Stage window (`StageRoot.tsx`) is a completely separate mount (`App.tsx`'s `route.kind ===
 * 'stage'` branch returns before `ConsoleShell`/this provider ever renders) and keeps calling the
 * bare `usePlaybackState()` hook directly with no provider involved — it was never one of the
 * three redundant instances to begin with.
 */
const PlaybackStateContext = createContext<PlaybackStateHook | null>(null);

export function PlaybackStateProvider({ children }: { children: ReactNode }) {
  const value = usePlaybackState('console');
  return createElement(PlaybackStateContext.Provider, { value }, children);
}

/** Reads the ONE shared cockpit `usePlaybackState()` instance — see `PlaybackStateProvider`'s own
 * doc comment. Every console-side consumer (`App.tsx`'s `ConsoleShell`, `OperatorConsole.tsx`,
 * `usePlaybackEngine.ts`) calls this instead of `usePlaybackState()` directly. */
export function usePlaybackStateContext(): PlaybackStateHook {
  const ctx = useContext(PlaybackStateContext);
  if (!ctx) throw new Error('usePlaybackStateContext() must be used within a <PlaybackStateProvider>.');
  return ctx;
}
